<template>
  <div>
    <div v-for="(image, index) in allPreviewImages" :key="index" class="relative mb-3">
      <div class="text-xs text-gray-600 mb-1">
        <span class="mx-auto">Page {{ index + 1 }}</span>
      </div>
      <div class="relative">
        <img
          :src="image.url"
          :width="image.metadata.width"
          :height="image.metadata.height"
          class="rounded border"
          loading="lazy"
        >
        <!-- Overlay to display fields on the preview -->
        <div class="top-0 bottom-0 left-0 right-0 absolute pointer-events-none">
          <div 
            v-for="(field, fieldIndex) in fieldsForPage(index)" 
            :key="fieldIndex"
            class="absolute rounded overflow-hidden"
            :class="[getBorderColor(field.field.submitter_uuid)]"
            :style="fieldPositionStyle(field.area)"
          >
            <div class="w-full h-full flex items-center justify-center overflow-hidden" :class="[getBgColor(field.field.submitter_uuid), 'bg-opacity-80']">
              <!-- Signature field -->              
              <div v-if="field.field.type === 'signature'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <path d="M15,25 C20,15 25,30 30,20 C35,10 40,25 45,20 C50,15 55,30 60,25 C65,20 70,15 75,25 C80,30 85,15 90,20" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              
              <!-- Initials field -->
              <div v-else-if="field.field.type === 'initials'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 60 40" class="w-full h-full max-h-8 opacity-50">
                  <path d="M15,10 L15,30 M25,10 L25,30 M15,10 L25,10 M35,10 L35,30 M45,10 L45,30 M35,20 L45,20" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              
              <!-- Date field -->
              <div v-else-if="field.field.type === 'date'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <line x1="30" y1="5" x2="30" y2="35" stroke="currentColor" stroke-width="2" />
                  <line x1="60" y1="5" x2="60" y2="35" stroke="currentColor" stroke-width="2" />
                  <text x="20" y="25" font-family="Arial" font-size="10" fill="currentColor" text-anchor="middle">DD</text>
                  <text x="45" y="25" font-family="Arial" font-size="10" fill="currentColor" text-anchor="middle">MM</text>
                  <text x="75" y="25" font-family="Arial" font-size="10" fill="currentColor" text-anchor="middle">YYYY</text>
                </svg>
              </div>
              
              <!-- Text field -->
              <div v-else-if="field.field.type === 'text'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <text x="50" y="25" font-family="Arial" font-size="14" fill="currentColor" text-anchor="middle">{{ field.field.name ? field.field.name.substring(0, 8) : 'Text' }}</text>
                </svg>
              </div>
              
              <!-- Number field -->
              <div v-else-if="field.field.type === 'number'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <text x="50" y="25" font-family="Arial" font-size="14" fill="currentColor" text-anchor="middle">123</text>
                </svg>
              </div>
              
              <!-- Checkbox field -->
              <div v-else-if="field.field.type === 'checkbox'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 40 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="5" y="5" width="30" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <path d="M10,20 L18,28 L30,10" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              
              <!-- Radio field -->
              <div v-else-if="field.field.type === 'radio'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 80 40" class="w-full h-full max-h-8 opacity-50">
                  <circle cx="20" cy="20" r="15" stroke="currentColor" fill="none" stroke-width="2" />
                  <circle cx="20" cy="20" r="7" fill="currentColor" />
                  <circle cx="60" cy="20" r="15" stroke="currentColor" fill="none" stroke-width="2" />
                </svg>
              </div>
              
              <!-- File/Image field -->
              <div v-else-if="['file', 'image'].includes(field.field.type)" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="20" y="5" width="60" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <path d="M45,15 L40,20 L45,25 M55,15 L60,20 L55,25 M48,25 L52,15" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              
              <!-- Multiple/Select field -->
              <div v-else-if="['multiple', 'select'].includes(field.field.type)" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <path d="M75,15 L85,20 L75,25" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                  <line x1="20" y1="15" x2="65" y2="15" stroke="currentColor" stroke-width="1.5" />
                  <line x1="20" y1="20" x2="60" y2="20" stroke="currentColor" stroke-width="1.5" />
                  <line x1="20" y1="25" x2="65" y2="25" stroke="currentColor" stroke-width="1.5" />
                </svg>
              </div>
              
              <!-- Stamp field -->
              <div v-else-if="field.field.type === 'stamp'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 60 40" class="w-full h-full max-h-8 opacity-50">
                  <circle cx="30" cy="20" r="15" stroke="currentColor" fill="none" stroke-width="2" stroke-dasharray="2 2" />
                  <text x="30" y="23" font-family="Arial" font-size="7" fill="currentColor" text-anchor="middle">STAMP</text>
                </svg>
              </div>
              
              <!-- Approval field -->
              <div v-else-if="field.field.type === 'approval'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <path d="M20,15 L30,25 L50,10" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                  <path d="M55,20 L65,20 M60,15 L60,25" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                  <path d="M70,15 L70,25 L80,25" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" />
                </svg>
              </div>
              
              <!-- Payment field -->
              <div v-else-if="field.field.type === 'payment'" class="flex items-center justify-center w-full h-full">
                <svg viewBox="0 0 100 40" class="w-full h-full max-h-8 opacity-50">
                  <rect x="10" y="5" width="80" height="30" rx="3" stroke="currentColor" fill="none" stroke-width="2" />
                  <rect x="15" y="12" width="30" height="5" rx="1" fill="currentColor" />
                  <line x1="15" y1="22" x2="45" y2="22" stroke="currentColor" stroke-width="2" />
                  <path d="M55,15 L55,25 M60,15 L60,25 M55,15 L60,15 M55,20 L60,20" stroke="currentColor" fill="none" stroke-width="1.5" />
                </svg>
              </div>
              
              <!-- Default for other field types -->
              <div v-else class="opacity-50 text-center text-xs truncate px-1">{{ field.field.name || field.field.type }}</div>
            </div>
          </div>
        </div>
        <div
          class="group flex justify-end cursor-pointer top-0 bottom-0 left-0 right-0 absolute p-1"
          @click="navigateToPage(index)"
        >
          <div
            v-if="editable && index === 0"
            class="flex justify-between w-full"
          >
            <div style="width: 26px" />
            <div class="">
              <ReplaceButton
                v-if="withReplaceButton"
                :template-id="template.id"
                :accept-file-types="acceptFileTypes"
                class="opacity-0 group-hover:opacity-100"
                @click.stop
                @success="$emit('replace', { replaceSchemaItem: item, ...$event })"
              />
            </div>
            <div
              class="flex flex-col justify-between opacity-0 group-hover:opacity-100"
            >
              <div>
                <button
                  class="btn border-base-200 bg-white text-base-content btn-xs rounded hover:text-base-100 hover:bg-base-content hover:border-base-content w-full transition-colors"
                  style="width: 24px; height: 24px"
                  @click.stop="$emit('remove', item)"
                >
                  &times;
                </button>
              </div>
              <div
                v-if="withArrows"
                class="flex flex-col space-y-1"
              >
                <button
                  class="btn border-base-200 bg-white text-base-content btn-xs rounded hover:text-base-100 hover:bg-base-content hover:border-base-content w-full transition-colors"
                  style="width: 24px; height: 24px"
                  @click.stop="$emit('up', item)"
                >
                  &uarr;
                </button>
                <button
                  class="btn border-base-200 bg-white text-base-content btn-xs rounded hover:text-base-100 hover:bg-base-content hover:border-base-content w-full transition-colors"
                  style="width: 24px; height: 24px"
                  @click.stop="$emit('down', item)"
                >
                  &darr;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex pb-2 pt-1.5">
      <Contenteditable
        :model-value="item.name"
        :icon-width="16"
        :editable="editable"
        style="max-width: 95%"
        class="mx-auto"
        @update:model-value="onUpdateName"
      />
    </div>
  </div>
</template>

<script>
import Contenteditable from './contenteditable'
import Upload from './upload'
import ReplaceButton from './replace'

export default {
  name: 'DocumentPreview',
  components: {
    Contenteditable,
    ReplaceButton
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    template: {
      type: Object,
      required: true
    },
    document: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    acceptFileTypes: {
      type: String,
      required: false,
      default: 'image/*, application/pdf'
    },
    withReplaceButton: {
      type: Boolean,
      required: true,
      default: true
    },
    withArrows: {
      type: Boolean,
      required: false,
      default: true
    },
    // Document identification props
    documentId: {
      type: [String, Number],
      required: false,
      default: null
    },
    documentIndex: {
      type: Number,
      required: false,
      default: -1
    },
    // The unique UUID of this document
    documentUuid: {
      type: String,
      required: true
    },
    // All fields in the template - we'll filter them ourselves
    allFields: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: ['scroll-to', 'change', 'remove', 'up', 'down', 'replace'],
  computed: {
    allPreviewImages () {
      return [...this.document.preview_images].sort((a, b) => parseInt(a.filename) - parseInt(b.filename))
    },
    previewImage () {
      // Maintain backwards compatibility
      return this.allPreviewImages[0]
    },
    borderColors() {
      // Exact border colors from area.vue component
      return [
        'border-red-500/80',
        'border-sky-500/80',
        'border-emerald-500/80',
        'border-yellow-300/80',
        'border-purple-600/80',
        'border-pink-500/80',
        'border-cyan-500/80',
        'border-orange-500/80',
        'border-lime-500/80',
        'border-indigo-500/80'
      ]
    },
    bgColors() {
      // Exact background colors from area.vue component
      return [
        'bg-red-100',
        'bg-sky-100',
        'bg-emerald-100',
        'bg-yellow-100',
        'bg-purple-100',
        'bg-pink-100',
        'bg-cyan-100',
        'bg-orange-100',
        'bg-lime-100',
        'bg-indigo-100'
      ]
    }
  },
  methods: {
    upload: Upload.methods.upload,
    onUpdateName (value) {
      this.item.name = value

      this.$emit('change')
    },
    navigateToPage (pageIndex) {
      console.log('Preview: Navigating to page index:', pageIndex)
      this.$emit('scroll-to', this.item, pageIndex)
    },
    fieldsForPage (pageIndex) {
      // We need to filter the fields ourselves based on document UUID
      const result = []
      
      // Process each field
      this.allFields.forEach(field => {
        if (!field.areas || !Array.isArray(field.areas)) return
        
        // Find areas matching this document UUID and page
        const matchingAreas = field.areas.filter(area => 
          area && 
          area.attachment_uuid === this.documentUuid && 
          area.page === pageIndex
        )
        
        // For each matching area, add a field entry to our result
        matchingAreas.forEach(area => {
          result.push({
            area: area,
            field: field
          })
        })
      })
      
      // Debug logs
      console.log(`Preview for document ${this.documentIndex} (UUID ${this.documentUuid}) page ${pageIndex} has ${result.length} fields`)
      if (result.length > 0) {
        console.log('Field types:', result.map(f => f.field.type).join(', '))
      }
      
      return result
    },
    fieldPositionStyle (area) {
      // Calculate position and size for field based on area properties
      return {
        left: `${area.x * 100}%`,
        top: `${area.y * 100}%`,
        width: `${area.w * 100}%`,
        height: `${area.h * 100}%`
      }
    },
    
    getBorderColor(submitterUuid) {
      // Get the border color for a specific submitter based on their UUID
      if (!submitterUuid || !this.template.submitters) return this.borderColors[0]
      
      // Find the submitter's index to determine their color
      const index = this.template.submitters.findIndex(s => s.uuid === submitterUuid)
      if (index === -1) return this.borderColors[0]
      
      // Use the submitter's index to select a color from the array
      return this.borderColors[index % this.borderColors.length]
    },
    
    getBgColor(submitterUuid) {
      // Get the background color for a specific submitter based on their UUID
      if (!submitterUuid || !this.template.submitters) return this.bgColors[0]
      
      // Find the submitter's index to determine their color
      const index = this.template.submitters.findIndex(s => s.uuid === submitterUuid)
      if (index === -1) return this.bgColors[0]
      
      // Use the submitter's index to select a color from the array
      return this.bgColors[index % this.bgColors.length]
    }
  }
}
</script>
